import axios from 'axios';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { useTheme } from '@/lib/useTheme';

import { toast } from 'sonner';
import { EmployeeAvatar } from '@/components/EmployeeAvatar';
import { formatDureeLive } from '@/lib/dureeFormat';  // 🎨 [v13.54] format non-ambigu
import {
  Home, MessageSquare, ClipboardCheck, Calendar, LayoutDashboard,
  Briefcase, CheckSquare, Clock, Menu, X, LogOut, ChevronRight,
  MapPin, AlertCircle, ShoppingCart, CalendarDays, Moon, Sun
} from 'lucide-react';
import { estProprietaire, aLeRole } from '@/lib/roles'; // 👥 E3b — rôles multiples

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

// Retourne les items de navigation selon le rôle
function getNavItems(role) {
  const msgs    = { path: '/messagerie',               icon: MessageSquare, label: 'Messages' };
  const rdv     = { path: '/crm/calendrier-rendez-vous', icon: Calendar,      label: 'Rendez-vous' };
  const menu    = { path: null, action: 'menu',        icon: Menu,          label: 'Menu' };
  const espace  = { path: '/mon-espace',               icon: Home,          label: 'Mon Espace' };
  const planning = { path: '/mon-espace', action: 'planning', icon: CalendarDays, label: 'Planning' };

  if (role === 'peintre') { // E3b-whitelist : configuration de barre par rôle principal
    return [
      espace,
      planning,
      null, // slot centre = Punch
      { path: '/mon-bonus', icon: ClipboardCheck, label: 'Bonus' },
      menu,
    ];
  }
  if (role === 'chef_equipe') { // E3b-whitelist : configuration de barre par rôle principal
    return [
      espace,
      planning,
      null, // slot centre = Punch
      { path: null, action: 'commande', icon: ShoppingCart, label: 'Commande' },
      menu,
    ];
  }
  if (role === 'installateur') { // E3b-whitelist : configuration de barre par rôle principal
    return [
      espace,
      { path: '/sous-traitants/lmds', icon: ClipboardCheck, label: 'LMDS' },
      msgs,
      menu,
    ];
  }
  if (role === 'charge_projet') { // E3b-whitelist : configuration de barre par rôle principal
    return [
      { path: '/crm/dossiers',           icon: Briefcase,  label: 'CRM' },
      { path: '/calendrier-production',  icon: Calendar,   label: 'Planning' },
      null,
      msgs, menu,
    ];
  }
  // proprietaire → Rendez-vous à la place de Messages
  if (role === 'proprietaire') { // E3b-whitelist : configuration de barre par rôle principal
    return [
      { path: '/',              icon: LayoutDashboard, label: 'Vue d\'ensemble' },
      { path: '/crm/dossiers',  icon: Briefcase,       label: 'CRM' },
      null,
      rdv, menu,
    ];
  }
  // admin, admin_general, admin_employe → Mon Espace
  return [
    { path: '/mon-espace',    icon: Home,            label: 'Mon Espace' },
    { path: '/crm/dossiers',  icon: Briefcase,       label: 'CRM' },
    null,
    msgs, menu,
  ];
}

// ─── Composant principal ──────────────────────────────────────────────────────
export function MobileBottomNav({ onToggleSidebar }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();

  const navigate = useNavigate();
  const location = useLocation();

  const [showMenuSheet, setShowMenuSheet] = useState(false);
  const [punchStatus, setPunchStatus] = useState(null);
  const [showPunchOut, setShowPunchOut] = useState(false);
  const [punchingOut, setPunchingOut] = useState(false);
  const [pauseLoading, setPauseLoading] = useState(false);
  const [proprioPauseDuration, setProprioPauseDuration] = useState(0.5);
  const [elapsed, setElapsed] = useState('');
  const [pauseElapsed, setPauseElapsed] = useState('');
  const [mobileConfig, setMobileConfig] = useState(null);

  // Charger la config mobile
  useEffect(() => {
    axios.get(`${API_URL}/api/configuration-mobile`).then(r => r.status === 200 ? r.data : null)
      .then(d => { if (d?.config) setMobileConfig(d.config); })
      .catch(() => {});
  }, []);

  // ─── Fetch statut punch ───────────────────────────────────────────────────
  const fetchPunchStatus = useCallback(async () => {
    if (!user?.employee_id) return;
    try {
      const res = await axios.get(`${API_URL}/api/punch/current/${user.employee_id}`);
      if (res.status === 200) setPunchStatus(res.data);
    } catch { /* offline graceful */ }
  }, [user?.employee_id]);

  useEffect(() => {
    fetchPunchStatus();
    const interval = setInterval(fetchPunchStatus, 30000); // refresh toutes les 30s
    return () => clearInterval(interval);
  }, [fetchPunchStatus]);

  // ─── Timer elapsed ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!punchStatus?.has_active_punch) { setElapsed(''); return; }
    const inTime = punchStatus.punch?.punch_in_time;
    const pauseStart = punchStatus.punch?.pause_start_time;
    if (!inTime) return;
    const update = () => {
      const pausedMs = parseFloat(punchStatus.punch?.heures_pause || 0) * 3600 * 1000;
      const activeEnd = pauseStart ? new Date(pauseStart).getTime() : Date.now();
      const diff = Math.floor((activeEnd - new Date(inTime).getTime() - pausedMs) / 1000);
      // 🎨 [Phase v13.54 — 2026-07-21] Format non-ambigu "8h 55min" (base 60)
      // pour lever la confusion avec les cumuls journée en base 100 décimale
      // "8,55h" affichés dans la même vue (paie CCQ).
      setElapsed(formatDureeLive(diff));
    };
    update();
    if (pauseStart) return; // timer figé si en pause
    const t = setInterval(update, 60000);
    return () => clearInterval(t);
  }, [punchStatus]);

  // ─── Timer pause ─────────────────────────────────────────────────────────
  useEffect(() => {
    const pauseStart = punchStatus?.punch?.pause_start_time;
    if (!pauseStart) { setPauseElapsed(''); return; }
    const update = () => {
      const diff = Math.floor((Date.now() - new Date(pauseStart).getTime()) / 1000);
      // 🎨 [Phase v13.54] Format non-ambigu — helper commun formatDureeLive.
      setPauseElapsed(formatDureeLive(diff));
    };
    update();
    const t = setInterval(update, 30000);
    return () => clearInterval(t);
  }, [punchStatus]);

  // ─── Punch OUT rapide ────────────────────────────────────────────────────
  const handleQuickPunchOut = async () => {
    if (!punchStatus?.punch?.id) return;
    setPunchingOut(true);
    try {
      // GPS obligatoire pour le punch out
      const { requestGPS } = await import('../lib/gpsUtils');
      const gps = await requestGPS({ required: true });
      if (!gps) { setPunchingOut(false); return; }

      const res = await axios.post(`${API_URL}/api/punch/out`, {
        punch_id: punchStatus.punch.id,
        heures_pause: isTerrain ? 0.5 : (punchStatus.punch.heures_pause || 0),
        latitude: gps.latitude,
        longitude: gps.longitude,
      });
      if (res.status === 200) {
        setPunchStatus({ has_active_punch: false, punch: null });
        setShowPunchOut(false);
        setElapsed('');
      }
    } catch { /* handle gracefully */ }
    finally { setPunchingOut(false); }
  };

  // ─── Pause START ──────────────────────────────────────────────────────────
  const handleStartPause = async () => {
    if (!punchStatus?.punch?.id) return;
    setPauseLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/punch/pause/start`, { punch_id: punchStatus.punch.id });
      if (res.status === 200) await fetchPunchStatus();
    } catch { /* graceful */ }
    finally { setPauseLoading(false); }
  };

  // ─── Pause END ────────────────────────────────────────────────────────────
  const handleEndPause = async () => {
    if (!punchStatus?.punch?.id) return;
    setPauseLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/punch/pause/end`, { punch_id: punchStatus.punch.id });
      if (res.status === 200) await fetchPunchStatus();
    } catch { /* graceful */ }
    finally { setPauseLoading(false); }
  };

  // ─── Propriétaire: Punch OUT avec durée de pause choisie ─────────────────
  const handleProprioPunchOut = async () => {
    if (!punchStatus?.punch?.id) return;
    setPunchingOut(true);
    try {
      const { requestGPS } = await import('../lib/gpsUtils');
      const gps = await requestGPS({ required: true });
      if (!gps) { setPunchingOut(false); return; }

      const res = await axios.post(`${API_URL}/api/punch/out`, {
          punch_id: punchStatus.punch.id,
          heures_pause: proprioPauseDuration,
          latitude: gps.latitude,
          longitude: gps.longitude,
        });
      if (res.status === 200) {
        setPunchStatus({ has_active_punch: false, punch: null });
        setShowPunchOut(false);
        setElapsed('');
      }
    } catch { /* graceful */ }
    finally { setPunchingOut(false); }
  };

  if (!user) return null;

  // Construire nav depuis config DB ou fallback
  const PAGE_MAP = {
    mon_espace: { path: '/mon-espace', icon: Home, label: 'Mon Espace' },
    vue_ensemble: { path: '/', icon: Home, label: "Vue d'ensemble" },
    crm: { path: '/crm/dossiers', icon: Briefcase, label: 'CRM' },
    rendez_vous: { path: '/crm/calendrier-rendez-vous', icon: Calendar, label: 'Rendez-vous' },
    planning: { path: '/mon-espace', action: 'planning', icon: CalendarDays, label: 'Planning' },
    bonus: { path: '/mon-bonus', icon: ClipboardCheck, label: 'Bonus' },
    lmds: { path: '/sous-traitants/lmds', icon: ClipboardCheck, label: 'LMDS' },
    messages: { path: '/messagerie', icon: MessageSquare, label: 'Messages' },
    entrepot: { path: '/entrepot', icon: ClipboardCheck, label: 'Entrepôt' },
  };
  const buildNav = (barKeys) => barKeys.filter(k => k && k !== 'aucun').map(key => {
    if (key === 'pointer') return null;
    if (key === 'menu') return { path: null, action: 'menu', icon: Menu, label: 'Menu' };
    if (key === 'commande') return { path: null, action: 'commande', icon: ShoppingCart, label: 'Commande' };
    return PAGE_MAP[key] || { path: '/', icon: Home, label: key };
  });

  // 🔧 Cat H — logique métier UI : un chef avec role='peintre' + role_terrain='chef_equipe' doit voir la navbar chef.
  // ⚠️ Point d'architecture à discuter (RCA V13.61 Vague 3) : ce fallback traite `role_terrain` comme override de `role`,
  // mais dans le reste de la codebase le `user.role` est la source unique. À harmoniser Wave 4 (fusion sous_traitant/installateur).
  // 👥 E3b — rôle principal (user.role) ; un chef d'équipe parmi ses rôles voit la barre chef
  const effectiveRole = (aLeRole(user, 'chef_equipe') && !estProprietaire(user) && !aLeRole(user, 'charge_projet', 'adjoint_admin')) ? 'chef_equipe' : user.role;
  const roleConfig = mobileConfig?.[effectiveRole];
  const navItems = roleConfig?.bar ? buildNav(roleConfig.bar) : getNavItems(effectiveRole);
  const isPunched = punchStatus?.has_active_punch;
  const isPaused = !!punchStatus?.punch?.pause_start_time;
  const hasPunch = roleConfig?.bar ? roleConfig.bar.includes('pointer') : true;
  // 🐛 BUG FIX RÉCURRENT (2026-05-06) — Avant : `isTerrain = hasPunch && !estProprietaire(user)`
  // classifiait à tort la CP/adjoints/admins comme terrain → handleQuickPunchOut envoyait
  // `heures_pause: 0.5` (30 min) même quand l'admin n'avait pris aucune pause, et l'UI mobile
  // privait la CP des boutons "Commencer ma pause" / "Reprendre". Le backend respectait déjà
  // la pause saisie ; le bug était purement côté frontend.
  // Les rôles ADMIN ne déclenchent JAMAIS d'auto-déduction : on respecte la pause réelle saisie
  // via les boutons "Commencer/terminer ma pause" (qui mettent à jour `heures_pause` en DB).
  // Cat H — logique métier UI : ces rôles ADMIN ne déclenchent JAMAIS d'auto-déduction 30min.
  // Le champ isTerrain ci-dessous est un dispatch UI (comportement pause), pas une restriction RBAC.
  const ADMIN_ROLES = ['proprietaire', 'admin', 'admin_general', 'admin_employe', 'charge_projet', 'adjoint_admin'];
  const isAdmin = ADMIN_ROLES.includes(user.role);
  const isTerrain = hasPunch && !isAdmin;
  const isProprietaire = estProprietaire(user); // Cat H — seul le proprio peut choisir sa durée de pause

  const handlePunchBtn = () => {
    if (isPunched) setShowPunchOut(true);
    else navigate('/pointage');
  };

  const handleNavItem = (item) => {
    if (!item) return;
    if (item.action === 'menu') { setShowMenuSheet(true); return; }
    if (item.action === 'planning') {
      navigate('/mon-espace', { state: { scrollTo: 'planning' } });
      return;
    }
    if (item.action === 'commande') {
      // Navigate to active bon de travail's commander des produits section
      if (punchStatus?.has_active_punch && punchStatus?.punch?.dossier_id && punchStatus?.punch?.bon_id) {
        navigate(`/crm/projets/${punchStatus.punch.dossier_id}?tab=bon-travail&bonId=${punchStatus.punch.bon_id}&scrollToCommande=1`);
      } else {
        toast.error('Vous devez être pounché sur un chantier pour commander des produits.');
      }
      return;
    }
    navigate(item.path);
  };

  const isActive = (path, action) => {
    if (!path && !action) return false;
    if (action === 'planning') return location.pathname === '/mon-espace';
    if (!path) return false;
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* ── Bottom Sheet Menu ── */}
      {showMenuSheet && (
        <div className="md:hidden fixed inset-0 z-[200] flex flex-col justify-end">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowMenuSheet(false)} />
          {/* Sheet */}
          <div className="relative bg-white dark:bg-slate-900 rounded-t-2xl shadow-2xl overflow-hidden"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
            {/* Handle bar */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-slate-300 rounded-full" />
            </div>

            {/* User info */}
            <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100">
              <EmployeeAvatar photoUrl={user?.photo_url} prenom={user?.prenom || ''} nom={user?.nom || ''} size="md" />
              <div>
                <p className="font-semibold text-slate-900 text-sm">{user?.prenom} {user?.nom}</p>
                <p className="text-xs text-slate-500 capitalize">{
                  { proprietaire: 'Propriétaire', charge_projet: 'Chargée de projet', chef_equipe: 'Chef d\'équipe', peintre: 'Peintre', installateur: 'Installateur LMDS', admin: 'Admin' }[user?.role] || user?.role
                }</p>
              </div>
            </div>

            {/* Actions */}
            <div className="px-6 py-4 space-y-2">
              <button onClick={() => { setShowMenuSheet(false); onToggleSidebar?.(); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-50 text-slate-700 hover:bg-slate-100 transition-colors">
                <Menu className="w-5 h-5 text-slate-500" />
                <span className="font-medium">Voir tout le menu</span>
                <ChevronRight className="w-4 h-4 text-slate-400 ml-auto" />
              </button>

              <button onClick={toggleTheme}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-50 text-slate-700 hover:bg-slate-100 transition-colors"
                data-testid="toggle-dark-mode-btn">
                {isDark ? <Sun className="w-5 h-5 text-amber-500" /> : <Moon className="w-5 h-5 text-slate-500" />}
                <span className="font-medium">{isDark ? 'Mode clair' : 'Mode sombre'}</span>
              </button>

              <button
                data-testid="mobile-logout-btn"
                onClick={() => { setShowMenuSheet(false); logout(); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 transition-colors">
                <LogOut className="w-5 h-5" />
                <span className="font-semibold">Se déconnecter</span>
              </button>
            </div>

            <div className="h-2" />
          </div>
        </div>
      )}

      {/* ── Barre de navigation inférieure ── */}
      <nav
        data-testid="mobile-bottom-nav"
        className="md:hidden fixed bottom-0 left-0 right-0 z-[90] bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="flex items-end justify-around px-1 pt-1 pb-2 relative">
          {navItems.map((item, idx) => {
            // Slot central = bouton Punch
            if (item === null) {
              return (
                <button
                  key="punch"
                  data-testid="mobile-punch-btn"
                  onClick={handlePunchBtn}
                  className={`flex flex-col items-center justify-center -mt-5 relative`}
                  style={{ minWidth: 64 }}
                >
                  {/* Cercle surélevé */}
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg border-4 border-white transition-all duration-300
                    ${isPaused
                      ? 'bg-amber-500 shadow-amber-200'
                      : isPunched
                        ? 'bg-emerald-500 shadow-emerald-200'
                        : 'bg-blue-600 shadow-blue-200'
                    }`}
                  >
                    <Clock className="w-6 h-6 text-white" />
                    {isPunched && !isPaused && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-400 rounded-full border-2 border-white animate-pulse" />
                    )}
                    {isPaused && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-400 rounded-full border-2 border-white" />
                    )}
                  </div>
                  <span className={`text-[10px] font-semibold mt-1 ${isPaused ? 'text-amber-600' : isPunched ? 'text-emerald-600' : 'text-blue-600'}`}>
                    {isPaused ? ('Pause ' + pauseElapsed) : isPunched ? (elapsed || 'En service') : 'Pointer'}
                  </span>
                </button>
              );
            }

            const active = isActive(item.path, item.action);
            const Icon = item.icon;
            return (
              <button
                key={item.action ? `${item.path || ''}_${item.action}` : item.path}
                onClick={() => handleNavItem(item)}
                className={`flex flex-col items-center justify-center gap-0.5 py-1 px-2 rounded-xl transition-all duration-150
                  ${active ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'}`}
                style={{ minWidth: 52 }}
              >
                <div className={`relative p-1.5 rounded-xl transition-all duration-150 ${active ? 'bg-blue-50' : ''}`}>
                  <Icon className="w-5 h-5" />
                  {/* Badge non-lu pour Messages */}
                  {item.path === '/messagerie' && (
                    <UnreadBadge />
                  )}
                </div>
                <span className="text-[10px] font-medium leading-none">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* ── Modal Punch OUT rapide ── */}
      {showPunchOut && (
        <div className="md:hidden fixed inset-0 z-[95] flex items-end">
          {/* Overlay */}
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowPunchOut(false)} />
          {/* Sheet */}
          <div className="relative w-full bg-white rounded-t-3xl shadow-2xl p-6 pb-10 z-10">
            <button
              onClick={() => setShowPunchOut(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isPaused ? 'bg-amber-100' : 'bg-emerald-100'}`}>
                <Clock className={`w-6 h-6 ${isPaused ? 'text-amber-600' : 'text-emerald-600'}`} />
              </div>
              <div>
                <p className="font-bold text-slate-900 text-lg">
                  {isPaused ? 'En pause' : 'En service'}
                </p>
                <p className="text-sm text-slate-500">
                  {punchStatus?.punch?.bon_titre || 'Bon de travail'} — {isPaused ? `Pause: ${pauseElapsed}` : elapsed}
                </p>
              </div>
            </div>

            {punchStatus?.punch?.adresse_chantier && (
              <div className="flex items-center gap-2 text-sm text-slate-500 bg-slate-50 rounded-xl px-3 py-2 mb-5">
                <MapPin className="w-4 h-4 text-slate-400 shrink-0" />
                <span className="truncate">{punchStatus.punch.adresse_chantier}</span>
              </div>
            )}

            {isTerrain ? (
              /* ── TERRAIN: Juste Journée terminée (30 min auto-déduites) ── */
              <>
                <p className="text-xs text-slate-400 text-center mb-3">30 min de pause déduites automatiquement</p>
                <button
                  data-testid="quick-punch-out-btn"
                  onClick={handleQuickPunchOut}
                  disabled={punchingOut}
                  className="w-full py-4 rounded-2xl bg-red-500 hover:bg-red-600 active:scale-98 text-white font-bold text-lg flex items-center justify-center gap-3 transition-all duration-200 shadow-md shadow-red-200 disabled:opacity-60"
                >
                  <LogOut className="w-5 h-5" />
                  {punchingOut ? 'Pointage en cours...' : 'Journée terminée'}
                </button>
              </>
            ) : isProprietaire ? (
              /* ── PROPRIÉTAIRE: Choisir la durée de pause ── */
              <>
                <p className="text-sm font-medium text-slate-700 mb-2">Durée de la pause :</p>
                <div className="flex gap-2 mb-4 flex-wrap">
                  {[
                    { label: '0 min', value: 0 },
                    { label: '15 min', value: 0.25 },
                    { label: '30 min', value: 0.5 },
                    { label: '45 min', value: 0.75 },
                    { label: '1h', value: 1 },
                    { label: '1h30', value: 1.5 },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setProprioPauseDuration(opt.value)}
                      className={`px-3 py-2 rounded-xl text-sm font-semibold transition-all ${
                        proprioPauseDuration === opt.value
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <button
                  data-testid="quick-punch-out-btn"
                  onClick={handleProprioPunchOut}
                  disabled={punchingOut}
                  className="w-full py-4 rounded-2xl bg-red-500 hover:bg-red-600 active:scale-98 text-white font-bold text-lg flex items-center justify-center gap-3 transition-all duration-200 shadow-md shadow-red-200 disabled:opacity-60"
                >
                  <LogOut className="w-5 h-5" />
                  {punchingOut ? 'Pointage en cours...' : 'Terminer ma journée'}
                </button>
              </>
            ) : isPaused ? (
              /* ── ADMIN NON-PROPRIO EN PAUSE → Reprendre ── */
              <button
                data-testid="quick-pause-end-btn"
                onClick={handleEndPause}
                disabled={pauseLoading}
                className="w-full py-4 rounded-2xl bg-emerald-500 hover:bg-emerald-600 active:scale-98 text-white font-bold text-lg flex items-center justify-center gap-3 transition-all duration-200 shadow-md shadow-emerald-200 disabled:opacity-60"
              >
                <Clock className="w-5 h-5" />
                {pauseLoading ? 'Chargement...' : 'Pause terminée — Reprendre'}
              </button>
            ) : (
              /* ── ADMIN NON-PROPRIO EN SERVICE → Pause + Journée terminée ── */
              <>
                <button
                  data-testid="quick-pause-start-btn"
                  onClick={handleStartPause}
                  disabled={pauseLoading}
                  className="w-full py-4 rounded-2xl bg-amber-500 hover:bg-amber-600 active:scale-98 text-white font-bold text-lg flex items-center justify-center gap-3 transition-all duration-200 shadow-md shadow-amber-200 disabled:opacity-60 mb-3"
                >
                  <Clock className="w-5 h-5" />
                  {pauseLoading ? 'Chargement...' : 'Commencer ma pause'}
                </button>

                <button
                  data-testid="quick-punch-out-btn"
                  onClick={handleQuickPunchOut}
                  disabled={punchingOut}
                  className="w-full py-4 rounded-2xl bg-red-500 hover:bg-red-600 active:scale-98 text-white font-bold text-lg flex items-center justify-center gap-3 transition-all duration-200 shadow-md shadow-red-200 disabled:opacity-60"
                >
                  <LogOut className="w-5 h-5" />
                  {punchingOut ? 'Pointage en cours...' : 'Journée terminée'}
                </button>
              </>
            )}

            <button
              onClick={() => { setShowPunchOut(false); navigate('/pointage'); }}
              className="w-full py-3 mt-3 text-slate-500 text-sm font-medium"
            >
              Voir tous mes pointages
              <ChevronRight className="inline w-4 h-4 ml-1" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Badge messages non lus ───────────────────────────────────────────────────
function UnreadBadge() {
  const { user } = useAuth();
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!user) return;
    const fetchUnread = async () => {
      try {
        const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
        if (!token) return;
        const myId = user.id || user.employee_id;
        if (!myId) return;  // 🔄 Phase B fix — endpoint exige user_id (sinon 422)
        const res = await axios.get(`${API_URL}/api/messagerie/conversations`, {
          params: { user_id: myId },
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.status !== 200) return;
        const d = res.data;
        let total = 0;
        (d.conversations || []).forEach(c => {
          const unread = c.unread_counts?.[myId] || 0;
          total += unread;
        });
        setCount(total);
      } catch { /* ignore */ }
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 60000);
    return () => clearInterval(t);
  }, [user]);

  if (count === 0) return null;
  return (
    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-white text-[9px] flex items-center justify-center font-bold border border-white">
      {count > 9 ? '9+' : count}
    </span>
  );
}
